import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  IUsePinnedCommentHook,
  TCommentAdd,
  TCommentDelete,
  TCommentUpdate,
  TCommentVote,
} from '../../../interfaces/hook.interface';
import { RQPostCommentKey } from '../../../constant/QueryKeyName';
import { IPostEachCommentResponse } from '../../../interfaces/post.interface';
import {
  deletePostComment,
  getPostPinnedComment,
  putPostPinComment,
  putUpdatePostComment,
  putVotePostComment,
} from '../../../apis/tms/post.api';

const useGetPinnedComment = (
  commentSectionId: string,
  setPinnedComment: React.Dispatch<
    React.SetStateAction<IPostEachCommentResponse | null>
  >
) => {
  return useQuery(
    [RQPostCommentKey.PostPinnedComment, commentSectionId],
    () => getPostPinnedComment(commentSectionId),
    {
      enabled: !!commentSectionId,
      retry: false,
      refetchOnMount: true,
      onSettled(data, error) {
        if (error) {
          setPinnedComment(null);
          return;
        }
        data && setPinnedComment(data);
      },
    }
  );
};

const usePinnedComment = (
  commentSectionId: string
): IUsePinnedCommentHook<IPostEachCommentResponse | null> => {
  const [pinnedComment, setPinnedComment] =
    useState<IPostEachCommentResponse | null>(null);

  const queryClient = useQueryClient();
  const infinityQueryReturn = useGetPinnedComment(
    commentSectionId,
    setPinnedComment
  );

  useEffect(() => {
    if (infinityQueryReturn.isLoading) {
      setPinnedComment(null);
    }
  }, [infinityQueryReturn.isLoading]);

  const placeholderAddComment: TCommentAdd = async () => {};

  const deletePinnedComment: TCommentDelete = useCallback(
    async (commentId: string) => {
      await deletePostComment(commentSectionId, commentId);
      setPinnedComment(null);
      infinityQueryReturn.refetch();
    },
    [commentSectionId, infinityQueryReturn]
  );

  const updatePinnedComment: TCommentUpdate = useCallback(
    async (commentId: string, content: string) => {
      const res = await (putUpdatePostComment({
        commentId,
        content,
        postId: commentSectionId,
      }) as unknown as Promise<IPostEachCommentResponse>);
      setPinnedComment(res);
    },
    [commentSectionId]
  );

  const votePinnedComment: TCommentVote = useCallback(
    async (commentId: string, upVote: boolean) => {
      await putVotePostComment(commentId);
      setPinnedComment((prev) =>
        prev
          ? {
              ...prev,
              votes: upVote ? prev.votes + 1 : prev.votes - 1,
            }
          : prev
      );
    },
    []
  );

  const unPinComment = useCallback(
    async (commentId: string) => {
      await putPostPinComment(commentSectionId, commentId);
      infinityQueryReturn.refetch();
      queryClient.resetQueries(RQPostCommentKey.PostCommentList);
      queryClient.resetQueries(RQPostCommentKey.PostSubComment);
    },
    [commentSectionId, infinityQueryReturn, queryClient]
  );

  return {
    infinityQueryReturn,
    pinnedComment,
    placeholderAddComment,
    deletePinnedComment,
    updatePinnedComment,
    votePinnedComment,
    unPinComment,
  };
};

export default usePinnedComment;
